import { Bell02, ChevronSelectorVertical, Edit01, Lightning01, MessageTextSquare01, PackageX } from '@untitled-ui/icons-react/build/cjs';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getDeliveryDefaults, IDeliveryPayload } from 'src/app/Dashboard/notificationDelivery';
import NotificationSettingsModal from 'src/app/Dashboard/NotificationSettingsModal';
import LightingIcon from 'src/assets/icons/Lighting.svg';
import ButtonCmp from 'src/components/ButtonCmp';
import OutsideClickHandler from 'src/components/OutsideClickHandler';
import TooltipCmp from 'src/components/TooltipCmp';
import { ORDER_STATUS, SHARE_TRACKING_LINK_STATUSES } from 'src/constants/common';
import { orderDetailsData, orderDetailsLoading, selectCurrentLegData, updateOrderField } from 'src/redux/orders.slice';
import { prependActivity } from 'src/redux/Quoting.slice';
import { AppDispatch, RootState } from 'src/redux/store';
import { INotificationTypes } from 'src/services/CustomerService';
import { logError } from 'src/services/LoggingService';
import { saveOrderNotificationSettings, updateOrderPriority } from 'src/services/OrderService';
import WalToast from 'src/utils/WalToast';

import CancelOrderModal from '../OrderView/modals/CancelOrderModal';
import EditOrderModal from '../OrderView/modals/EditOrderModal';
import ShareTrackingLinkModal from '../OrderView/modals/ShareTrackingLinkModal';

interface IOrderHeaderActionsProps {
    /** Route-level order id — the same id OrderView/Radar receive in their URL param. */
    orderDbId: string;
    /**
     * Button theme class. Defaults to the order page's indigo outline; the radar header
     * passes 'btn_secondary_black' so the cluster matches the icon buttons beside it.
     */
    buttonClassName?: string;
    /**
     * Show the Order Actions dropdown (Edit/Cancel). Defaults to true (order page);
     * the radar header passes false to hide it there without removing the feature.
     */
    showOrderActions?: boolean;
    /**
     * Render Mark as Priority / Notification Settings as square icon-only buttons with
     * their label in a tooltip (same pattern as the radar header's Map Theme button).
     * Defaults to false — the order page keeps the labeled buttons.
     */
    isIconOnly?: boolean;
}

/**
 * OrderHeaderActions — the order-level header buttons (Mark as Priority, Notification
 * Settings, Order Actions dropdown) plus the modals they open.
 *
 * Extracted from OrderView so the Radar detail page can render the exact same cluster.
 * Reads the order from orders.slice (orderDetailsData) — the host page is responsible
 * for dispatching getOrderDetailsAsync(orderDbId) so that slice is populated.
 */
const OrderHeaderActions = ({ orderDbId, buttonClassName = 'btn-outline-primary', showOrderActions = true, isIconOnly = false }: IOrderHeaderActionsProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const isLoading = useSelector(orderDetailsLoading);
    const order = useSelector(orderDetailsData);
    const currentLegData = useSelector(selectCurrentLegData);
    const currentUser = useSelector((state: RootState) => state.auth.currentUser);

    // Feature gate: resolved 3-tier visibility comes from the order's orderVisibility (returned by orderDetail.service.ts)
    const isTextNotificationFeatureVisible = (order as any)?.orderVisibility?.textNotification !== false;

    // Offered while the shipment is on the road AND after delivery until the tracking link's
    // one-week grace period closes (client, 2026-10-08 — it used to disappear the moment a load was
    // marked delivered, while the link stayed live for days after). The boundary is the server's:
    // isShareLinkExpired comes from orderDetail.service.ts running the same rule the share pages
    // enforce, so the button is never offered for a link that would already be dead.
    const isShareTrackingLinkVisible = SHARE_TRACKING_LINK_STATUSES.includes(order?.orderStatus as string) && !(order as any)?.isShareLinkExpired;

    const [isShowOrderActionsDropDown, setIsShowOrderActionsDropDown] = useState(false);
    const [isShowEditOrderModal, setIsShowEditOrderModal] = useState(false);
    const [isShowCancelOrderModal, setIsShowCancelOrderModal] = useState(false);
    const [isShowTextNotificationModal, setIsShowTextNotificationModal] = useState(false);
    const [isShowShareTrackingLinkModal, setIsShowShareTrackingLinkModal] = useState(false);
    const [isPriorityLoading, setIsPriorityLoading] = useState(false);

    // Pre-fill: order-wise settings first; fall back to user's global notification settings.
    const notificationExistingData = useMemo(() => {
        const orderWise = (order?.textNotification as any)?.notificationTypes?.notificationTypes;
        if (orderWise) return orderWise;

        return currentUser?.notificationSettings?.notificationTypes ?? null;
    }, [order?.textNotification, currentUser?.notificationSettings]);

    const isNotificationEnabled = useMemo(() => {
        const notifData = order?.textNotification as any;
        if (!notifData) return false;

        // Order-level notificationTypes JSONB
        if (notifData.notificationTypes?.notificationTypes) {
            return Object.values(notifData.notificationTypes.notificationTypes).some((entry: any) => entry?.enabled === true);
        }

        return false;
    }, [order?.textNotification]);

    const handleMarkAsPriority = async () => {
        if (!order) return;

        const newPriorityValue = !order.isPriority;
        setIsPriorityLoading(true);

        try {
            const response = await updateOrderPriority(orderDbId, newPriorityValue);
            dispatch(updateOrderField({ field: 'isPriority', value: newPriorityValue }));
            const activity = response?.data?.data?.activity ?? response?.data?.activity;
            if (activity) dispatch(prependActivity(activity));
            WalToast.success(newPriorityValue ? (t('Orders.Marked_as_priority') as string) : (t('Orders.Reverted_to_standard') as string), '', '!w-[260px]');
        } catch (err: any) {
            await logError(err, {
                errorMessage: err?.message || 'Error updating order priority',
                errorLocation: 'OrderHeaderActions: handleMarkAsPriority',
                storeInDatabase: true,
                logToConsole: true,
            });
            WalToast.error('Priority update failed');
        } finally {
            setIsPriorityLoading(false);
        }
    };

    // Send to (this load only) prefill: this user's saved per-load addresses, else their profile (cp-meeting-changes F2).
    const notificationDelivery = useMemo(() => getDeliveryDefaults((order?.textNotification as any)?.notificationTypes, currentUser), [order?.textNotification, currentUser]);

    const handleSaveOrderNotifications = async (notificationTypes: INotificationTypes, delivery?: IDeliveryPayload) => {
        const response = await saveOrderNotificationSettings(orderDbId, notificationTypes, delivery);
        const metadata = response?.data?.data?.metadata ?? response?.data?.metadata ?? null;
        dispatch(updateOrderField({ field: 'textNotification', value: metadata }));
    };

    return (
        <>
            <div className={isIconOnly ? 'flex items-center sm:gap-x-2.5 gap-x-2' : 'flex gap-x-3'}>
                {/* Share Tracking Link — sits ahead of Mark as Priority. Stays labelled even when
                    `isIconOnly` collapses the buttons after it — matches the WAL radar header, where
                    this is the only labelled button in the row.

                    Shown ONLY while the shipment is actually moving (dispatched / in-transit), per
                    client 2026-10-07, matching WALHQ's order detail and radar headers. It used to be
                    shown for every status because the recipients section was useful before a link
                    existed; a delivered shipment has nothing left to follow, so that no longer
                    outweighs offering a link to a page which stops answering 7 days after delivery.
                    This component is rendered by BOTH the order detail page and the Track/Radar
                    detail page, so this one gate covers both. */}
                {isShareTrackingLinkVisible && (
                    <ButtonCmp
                        className={`${buttonClassName} ${isLoading ? 'custom-loading' : ''}`}
                        icon={<MessageTextSquare01 className="w-4 h-4" />}
                        onClick={() => setIsShowShareTrackingLinkModal(true)}
                        disabled={isLoading}
                    >
                        {t('Orders.Share_Tracking_Link.Title')}
                    </ButtonCmp>
                )}
                {isIconOnly ? (
                    <TooltipCmp message={order?.isPriority ? t('Orders.Revert_to_standard') : t('Orders.Mark_as_Priority')} childrenClassName="flex">
                        <ButtonCmp
                            size="square-sm"
                            className={`${buttonClassName} ${isLoading ? 'custom-loading' : ''}`}
                            icon={order?.isPriority ? <img src={LightingIcon} className="w-3.5 h-3.5" alt="Priority" /> : <Lightning01 className="w-4 h-4" />}
                            onClick={handleMarkAsPriority}
                            loading={isPriorityLoading}
                            disabled={isLoading || isPriorityLoading || order?.orderStatus === ORDER_STATUS.CANCELLED}
                        >
                            <></>
                        </ButtonCmp>
                    </TooltipCmp>
                ) : (
                    <ButtonCmp
                        className={`${buttonClassName} ${isLoading ? 'custom-loading' : ''}`}
                        icon={order?.isPriority ? <img src={LightingIcon} className="w-3.5 h-3.5" alt="Priority" /> : <Lightning01 className="w-4 h-4" />}
                        onClick={handleMarkAsPriority}
                        loading={isPriorityLoading}
                        disabled={isLoading || isPriorityLoading || order?.orderStatus === ORDER_STATUS.CANCELLED}
                    >
                        {order?.isPriority ? t('Orders.Revert_to_standard') : t('Orders.Mark_as_Priority')}
                    </ButtonCmp>
                )}
                {isTextNotificationFeatureVisible &&
                    (isIconOnly ? (
                        <TooltipCmp message={t('Dashboard.NotificationSettings.Title')} childrenClassName="flex">
                            <ButtonCmp
                                size="square-sm"
                                className={`${isNotificationEnabled ? 'bg-white border border-[#A4BCFD] shadow-xs rounded-md hover:bg-[#EEF4FF]' : buttonClassName} ${
                                    isLoading ? 'custom-loading' : ''
                                }`}
                                icon={<Bell02 className={`w-4 h-4 ${isNotificationEnabled ? 'text-[#3538CD]' : ''}`} />}
                                onClick={() => setIsShowTextNotificationModal(true)}
                                disabled={isLoading || order?.orderStatus === ORDER_STATUS.CANCELLED}
                            >
                                <></>
                            </ButtonCmp>
                        </TooltipCmp>
                    ) : (
                        <ButtonCmp
                            className={`${isNotificationEnabled ? 'bg-white border border-[#A4BCFD] shadow-xs font-semibold text-[#3538CD] text-xs rounded-md hover:bg-[#EEF4FF]' : buttonClassName} ${
                                isLoading ? 'custom-loading' : ''
                            }`}
                            icon={<Bell02 className={`w-4 h-4 ${isNotificationEnabled ? 'text-[#3538CD]' : ''}`} />}
                            onClick={() => setIsShowTextNotificationModal(true)}
                            disabled={isLoading || order?.orderStatus === ORDER_STATUS.CANCELLED}
                        >
                            {t('Dashboard.NotificationSettings.Title')}
                        </ButtonCmp>
                    ))}
                {showOrderActions &&
                    ![ORDER_STATUS.DELIVERED, ORDER_STATUS.COMPLETED, ORDER_STATUS.INVOICE_CREATED, ORDER_STATUS.INVOICE_SENT, ORDER_STATUS.INVOICE_PAID, ORDER_STATUS.CANCELLED].includes(
                        order?.orderStatus as any
                    ) && (
                        <OutsideClickHandler onOutsideClick={() => setIsShowOrderActionsDropDown(false)} containerClassName="relative">
                            <ButtonCmp
                                className={`${buttonClassName} ${isLoading ? 'custom-loading' : ''}`}
                                onClick={() => !isLoading && setIsShowOrderActionsDropDown((prev) => !prev)}
                                disabled={isLoading}
                            >
                                Order Actions
                                <ChevronSelectorVertical className={`w-3.5 h-3.5 transition-transform duration-200`} />
                            </ButtonCmp>
                            <ul
                                className={`shadow text-xs rounded-lg break-words cursor-pointer empty:hidden  bg-white border border-utilityGray200 p-1 my-1.5 z-[1] min-w-[150px] w-fit right-0 absolute leading-[18px] ${
                                    isShowOrderActionsDropDown ? '' : 'hidden'
                                }`}
                            >
                                <li
                                    className="text-xs leading-[1.5] font-normal text-grayLight900 rounded-md bg-transparent hover:bg-white800 p-1 transition-all duration-[0.3s] cursor-pointer flex items-center gap-x-2"
                                    onClick={() => {
                                        setIsShowOrderActionsDropDown(false);
                                        setIsShowEditOrderModal(true);
                                    }}
                                >
                                    <Edit01 className="w-3.5 h-3.5 text-grayLight900 flex-none" />
                                    <p className="flex-1">Edit Order</p>
                                </li>
                                <li
                                    className={`text-xs leading-[1.5] font-normal rounded-md bg-transparent p-1 transition-all duration-[0.3s] cursor-pointer flex items-center gap-x-2 ${
                                        order?.cancelRequest ? 'opacity-50 cursor-not-allowed' : 'text-textSecondary hover:bg-gray50 cursor-pointer'
                                    }`}
                                    onClick={() => {
                                        if (!order?.cancelRequest) {
                                            setIsShowOrderActionsDropDown(false);
                                            setIsShowCancelOrderModal(true);
                                        }
                                    }}
                                >
                                    <PackageX className="w-3.5 h-3.5 min-w-3.5 min-h-3.5 text-fgErrorPrimary flex-none" />
                                    <p className="flex-1 text-fgErrorPrimary text-nowrap">{order?.cancelRequest ? 'Cancellation Requested' : 'Cancel Order'}</p>
                                </li>
                            </ul>
                        </OutsideClickHandler>
                    )}
            </div>
            {isShowEditOrderModal && (
                <EditOrderModal
                    handleClose={() => setIsShowEditOrderModal(false)}
                    orderId={orderDbId}
                    orderType={order?.orderType}
                    existingLineItems={currentLegData?.order_dimensions}
                    onSuccess={(_editRequest, activity) => {
                        if (activity) dispatch(prependActivity(activity));
                    }}
                />
            )}
            {isShowCancelOrderModal && (
                <CancelOrderModal
                    handleClose={() => setIsShowCancelOrderModal(false)}
                    orderId={orderDbId}
                    onSuccess={(cancelRequest, activity) => {
                        dispatch(updateOrderField({ field: 'cancelRequest', value: cancelRequest }));
                        if (activity) dispatch(prependActivity(activity));
                    }}
                />
            )}
            {isShowTextNotificationModal && (
                <NotificationSettingsModal
                    handleClose={() => setIsShowTextNotificationModal(false)}
                    existingData={notificationExistingData}
                    onSave={handleSaveOrderNotifications}
                    delivery={notificationDelivery}
                />
            )}
            {isShowShareTrackingLinkModal && (
                <ShareTrackingLinkModal orderDbId={orderDbId} orderStatus={order?.orderStatus} walId={order?.orderId} handleClose={() => setIsShowShareTrackingLinkModal(false)} />
            )}
        </>
    );
};

export default OrderHeaderActions;
